import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-warm">
      {/* Header */}
      <nav className="bg-white/90 dark:bg-slate-950/80 backdrop-blur-sm border-b border-rose-100 dark:border-slate-800 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 dark:text-slate-200 hover:text-rose-600"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            
            <div className="flex items-center space-x-2">
              <Shield className="w-6 h-6 text-rose-600" />
              <span className="font-playfair text-xl font-bold text-gray-900 dark:text-white">
                Privacy Policy
              </span>
            </div>

            <div className="w-20"></div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="glass rounded-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h1 className="font-playfair text-4xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-600">
              How we protect and handle your personal information
            </p>
            <div className="text-sm text-gray-500 mt-4">
              Last updated: January 2025
            </div>
          </div>

          <div className="space-y-8">
            {/* Introduction */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="w-6 h-6 text-rose-600" />
                <h2 className="font-playfair text-2xl font-semibold text-gray-900">
                  Our Commitment to Your Privacy
                </h2>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Unity Love ("we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our dating platform designed for African American and African couples seeking connections with single women.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <Database className="w-6 h-6 text-rose-600" />
                <h2 className="font-playfair text-2xl font-semibold text-gray-900">
                  Information We Collect
                </h2>
              </div>
              
              <div className="space-y-4">
                <div className="bg-rose-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Personal Information You Provide</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Email address and password</li>
                    <li>• Names, ages, and profile information</li>
                    <li>• Location data (city/state)</li>
                    <li>• Cultural background and preferences</li>
                    <li>• Photos and profile descriptions</li>
                    <li>• Messages and communications</li>
                    <li>• Two-factor authentication information (if enabled)</li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Automatically Collected Information</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Device information and IP address</li>
                    <li>• Browser type and version</li>
                    <li>• Usage patterns and app interactions</li>
                    <li>• Log data and technical information</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Information */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <Lock className="w-6 h-6 text-rose-600" />
                <h2 className="font-playfair text-2xl font-semibold text-gray-900">
                  How We Use Your Information
                </h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">Core Platform Functions</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Creating and managing your profile</li>
                    <li>• Matching you with compatible users</li>
                    <li>• Facilitating messaging between matches</li>
                    <li>• Providing customer support</li>
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">Safety & Security</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Verifying user authenticity</li>
                    <li>• Preventing fraud and abuse</li>
                    <li>• Enforcing community guidelines</li>
                    <li>• Protecting user safety</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data Sharing */}
            <section>
              <div className="flex items-center space-x-3 mb-4">
                <Eye className="w-6 h-6 text-rose-600" />
                <h2 className="font-playfair text-2xl font-semibold text-gray-900">
                  Information Sharing and Disclosure
                </h2>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">🔒 We DO NOT Sell Your Data</h3>
                <p className="text-gray-700">
                  Unity Love does not sell, trade, or rent your personal information to third parties for marketing purposes.
                </p>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">We may share information only in these circumstances:</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• <strong>With Other Users:</strong> Profile information visible to potential matches</li>
                  <li>• <strong>Service Providers:</strong> Trusted vendors who help us operate the platform</li>
                  <li>• <strong>Legal Requirements:</strong> When required by law or to protect safety</li>
                  <li>• <strong>Business Transfers:</strong> In case of merger or acquisition (with notice)</li>
                </ul>
              </div>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-4">
                Your Privacy Rights
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Access & Control</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Access your personal data</li>
                    <li>• Update or correct information</li>
                    <li>• Delete your account and data</li>
                    <li>• Export your data</li>
                  </ul>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">GDPR & CCPA Rights</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Opt-out of data processing</li>
                    <li>• Restrict data usage</li>
                    <li>• Data portability</li>
                    <li>• Non-discrimination protection</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-4">
                Data Security
              </h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-4">
                  We implement industry-standard security measures to protect your information:
                </p>
                <ul className="text-gray-700 space-y-1">
                  <li>• Encryption of data in transit and at rest</li>
                  <li>• Secure password hashing</li>
                  <li>• Two-factor authentication support</li>
                  <li>• Regular security audits and monitoring</li>
                  <li>• Limited access to personal data by employees</li>
                </ul>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-4">
                Contact Us
              </h2>
              <div className="bg-rose-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-3">
                  If you have questions about this Privacy Policy or your data:
                </p>
                <div className="space-y-2 text-gray-700">
                  <div>📧 Email: privacy@joinunitylove.com</div>
                </div>
                <p className="text-gray-600 text-sm mt-4">
                  We will respond to privacy inquiries within 30 days as required by applicable law.
                </p>
              </div>
            </section>

            {/* Policy Updates */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-4">
                Policy Updates
              </h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by email or through the platform. Your continued use of Unity Love after such modifications constitutes your acceptance of the updated Privacy Policy.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;